/* ============================================================================
   apiBase - single source of truth for the backend URL.
   ----------------------------------------------------------------------------
   Resolution order (most reliable first):

   1. window.__NEXUS_DEX_API_BASE__ - set by an inline <script> in index.html.
      This is the only path Capacitor builds rely on. The string ships in the
      HTML file inside dist/public, gets copied by `cap sync` into
      ios/App/App/public/index.html, and is read at runtime. No Vite env
      substitution involved, no .env.production dependency, no chance of
      silent fall-through. The bundle on disk can be inspected directly to
      confirm the exact URL.

   2. import.meta.env.VITE_API_BASE_URL - retained as a backup path for any
      build pipeline that wires the env var.

   3. Empty string - same-origin relative requests, used by `vite serve` in
      local development where the dev proxy forwards /api -> :5000 .

   Inside Capacitor (iOS / Android) the webview origin is https://localhost
   (because capacitor.config.ts sets iosScheme: "https"). A relative /api/...
   request resolves to https://localhost/api/... which iOS WKWebView then
   rejects with "The string did not match the expected pattern" before any
   network call is made. So API_BASE MUST be a non-empty absolute https URL
   in native builds. We assert this loudly via assertApiBaseConfigured().
   ========================================================================= */

declare global {
  interface Window {
    __NEXUS_DEX_API_BASE__?: string;
  }
}

function resolveApiBase(): string {
  // 1. Runtime injection from index.html.
  if (typeof window !== "undefined" && typeof window.__NEXUS_DEX_API_BASE__ === "string") {
    const v = window.__NEXUS_DEX_API_BASE__.trim();
    if (v.length > 0) return v.replace(/\/$/, "");
  }
  // 2. Build-time Vite env.
  const fromEnv = (import.meta.env?.VITE_API_BASE_URL as string | undefined) || "";
  if (fromEnv.trim().length > 0) return fromEnv.trim().replace(/\/$/, "");
  // 3. Same-origin (dev only).
  return "";
}

export const API_BASE = resolveApiBase();

/**
 * True if the page is being served by a Capacitor native webview. The
 * Capacitor runtime injects `window.Capacitor.isNativePlatform()` so this is
 * stable across iOS and Android.
 */
export function isCapacitorNative(): boolean {
  if (typeof window === "undefined") return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cap = (window as any).Capacitor;
  return Boolean(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
}

/**
 * Throws a developer-friendly error if API_BASE is empty in a native build.
 * Call this at app startup so a misconfigured bundle screams immediately
 * instead of silently failing on the first fetch with a cryptic NSURL error.
 */
export function assertApiBaseConfigured(): void {
  if (!API_BASE && isCapacitorNative()) {
    const msg =
      "Nexus Dex configuration error: API base URL is empty. " +
      "Native builds require window.__NEXUS_DEX_API_BASE__ to be set in " +
      "index.html. Rebuild the web bundle and run 'npx cap sync ios'.";
    // Log loudly so it shows up in Safari Web Inspector.
    // eslint-disable-next-line no-console
    console.error(msg);
    throw new Error(msg);
  }
}

